/**
 * Shared styled components for the States Add/Edit forms.
 *
 * Extracted from AddStates.jsx and EditState.jsx to eliminate duplication.
 * Follows the same pattern as Users/Styles.js.
 */
import React from "react";
import { Button, Form, Typography } from "antd";
import styled from "styled-components";

const { Text } = Typography;

/* ---------- Form layout ---------- */

export const CompactIntro = styled(Text)`
  display: block;
  margin-bottom: 8px;
`;

export const V2Form = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

export const TwoPaneLayout = styled.div`
  display: flex;
  flex: 1 1 auto;
  gap: 16px;
  min-height: 0;
  overflow: hidden;
`;

export const LeftPane = styled.div`
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex: 0 0 calc(40% - 8px);
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding-right: 16px;

  .ant-form-item {
    margin-bottom: 8px;
  }
`;

export const RightPane = styled.div`
  display: flex;
  flex: 0 0 calc(60% - 8px);
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

/* ---------- Category panel ---------- */

export const V2AddCategoryPanel = styled.div`
  border-top: 1px solid #f0f0f0;
  flex: 0 0 auto;
  margin-top: 2px;
  padding-top: 8px;

  .ant-form-item {
    margin-bottom: 8px;
  }
`;

export const V2AddCategoryActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* ---------- Category list ---------- */

export const CategoryListHeader = styled.div`
  align-items: baseline;
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const CategoryList = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
`;

export const CategoryCard = styled.div`
  align-items: flex-start;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  padding: 10px 12px;
`;

export const CategoryCardContent = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const CategoryName = styled(Text)`
  display: block;
  margin-bottom: 6px;
`;

export const MetadataGrid = styled.div`
  display: grid;
  gap: 4px 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const MetadataItem = styled.div`
  min-width: 0;
`;

/* ---------- Actions bar ---------- */

export const V2ActionsBar = styled.div`
  background: #fff;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 12px 0;
`;

export const StyledButton = styled(Button)`
  margin-left: 8px;
  width: 96px;
`;

/* ---------- Presentational helpers ---------- */

export const CategoryMetadata = ({ label, value }) => (
  <MetadataItem>
    <Text type="secondary">{label}: </Text>
    <Text>{value || "—"}</Text>
  </MetadataItem>
);

/* ---------- BulkUpdate ---------- */

export const BulkUpdateForm = styled(Form)`
  margin: 0 auto;
  max-width: 45%;
`;

/* Wraps a labeled Form.Item so a CheckboxAddon can be pinned to the label row. */
export const SelectWithAddon = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

/* Absolutely-positioned container for the "Select All" checkbox — sits in the label row, right-aligned. */
export const CheckboxAddon = styled.div`
  align-items: center;
  display: flex;
  height: 22px; /* matches antd's default label line-height */
  position: absolute;
  right: 0;
  top: 0;
`;

export const SwitchLabelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

export const InlineSwitchItem = styled(Form.Item)`
  margin-bottom: 0;
`;
